import React from 'react';

import { contentTypeAdminContent } from '@lumapps/content-types-admin-content/routes';
import { ANCHOR_FROM_FO } from '@lumapps/contents/constants';
import { getCurrentContent } from '@lumapps/contents/ducks/selectors';
import { contentView, homePageView } from '@lumapps/contents/routes';
import { LegacyContentStatus } from '@lumapps/contents/types';
import { CONTENT_EDITOR_TOPBAR_SCOPE } from '@lumapps/designer/constants';
import { getCurrentInstance } from '@lumapps/instance/ducks/selectors';
import { usePromiseConfirmDialog } from '@lumapps/lumx-dialogs/hooks/usePromiseConfirmDialog';
import { useSelector } from '@lumapps/redux/react';
import { useRouter, useQueryParams } from '@lumapps/router';
import { isContentInSpaceFeatureEnabled } from '@lumapps/spaces/ducks/selectors';
import { useSpaceDataFromRedux } from '@lumapps/spaces/hooks/useSpaceDataFromRedux';
import { spaceView } from '@lumapps/spaces/routes';
import { SpaceViews } from '@lumapps/spaces/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { isLayoutEnabled as isLayoutEnabledSelector } from '@lumapps/widget-layout/ducks/selectors';

interface Params {
    /** Legacy content editor has changes */
    hasChanges?: () => boolean;
}

const SCOPE = `${CONTENT_EDITOR_TOPBAR_SCOPE}-close-confirm-dialog`;

/**
 * Callback when closing the content editor to determine where the user should be redirected.
 */
export function useCloseContentEditorRedirect(id?: string, params: Params = {}) {
    const { hasChanges } = params;
    const { redirect } = useRouter();
    const { translateKey, translateObject } = useTranslate();
    const instance = useSelector(getCurrentInstance);
    const isLayoutEnabled = useSelector(isLayoutEnabledSelector);
    const currentContent = useSelector(getCurrentContent);
    const isContentInSpaceEnabled = useSelector(isContentInSpaceFeatureEnabled);
    const hasALiveVersion = currentContent.status === LegacyContentStatus.live;
    const contentTypeId = currentContent.customContentTypeDetails?.id;
    const closeConfirmDialog = usePromiseConfirmDialog();

    // Check if we're in a space content context (via query params)
    const { spaceId } = useQueryParams();

    // Get space data (including slug) needed for redirect URLs.
    // Only fetch when content-in-space is enabled, matching where the data is actually used below.
    const { spaceData } = useSpaceDataFromRedux(spaceId, { enabled: isContentInSpaceEnabled });

    // Check if coming from front-office (done once on mount)
    const fromFrontOffice = React.useMemo(() => {
        const fromFromOffice = window.location.hash === `#${ANCHOR_FROM_FO}`;
        if (fromFromOffice) {
            // Remove since it's not needed anymore
            window.location.hash = '';
        }
        return fromFromOffice;
    }, []);

    const closeContentEditor = React.useCallback(async () => {
        // Confirm close when content hasChanges
        if (hasChanges?.()) {
            const confirmed = await closeConfirmDialog.open({
                id: SCOPE,
                title: translateKey(GLOBAL.UNSAVED_MODIFICATIONS),
                confirmMessage: translateKey(GLOBAL.UNSAVED_MODIFICATIONS_DESCRIPTION),
                cancelProps: { label: translateKey(GLOBAL.CANCEL) },
                confirmProps: { label: translateKey(GLOBAL.CONTINUE) },
            });
            if (!confirmed) {
                // Cancel close
                return;
            }
        }

        // Check if we're in the space content context (creation or edit, only if feature flag is enabled)
        // Space slug is retrieved from Redux state
        if (isContentInSpaceEnabled && spaceId && spaceData?.slug) {
            const spaceSlug = spaceData.slug;

            // If content has been saved (has an ID), redirect to space content view
            if (id) {
                redirect(
                    spaceView({
                        params: {
                            slug: translateObject(spaceSlug) as string,
                            id: spaceId,
                            view: SpaceViews.content,
                            identifier: id,
                        },
                    }),
                    { shouldSkipLegacyChangeConfirm: true },
                );
                return;
            }

            // If content hasn't been saved yet (creation flow only), redirect to space home
            redirect(
                spaceView({
                    params: {
                        slug: translateObject(spaceSlug) as string,
                        id: spaceId,
                        view: SpaceViews.home,
                    },
                }),
                { shouldSkipLegacyChangeConfirm: true },
            );
            return;
        }

        // Has no live version (new content or has never been published)
        if (contentTypeId && !hasALiveVersion) {
            // Go to home page
            if (fromFrontOffice) {
                redirect(homePageView({}), { shouldSkipLegacyChangeConfirm: true });
                return;
            }

            // Go to the admin of contents for the content type
            redirect(contentTypeAdminContent(contentTypeId), { shouldSkipLegacyChangeConfirm: true });
            return;
        }

        // Go to content view
        redirect(
            contentView({
                isLayoutEnabled,
                to: {
                    id,
                    slug: translateObject(currentContent.slug) as string,
                    isV2Compatible: currentContent.template?.isV2Compatible,
                    isHomePage: id === instance?.homePage,
                },
            }),
            { shouldSkipLegacyChangeConfirm: true },
        );
    }, [
        closeConfirmDialog,
        contentTypeId,
        currentContent.slug,
        currentContent.template?.isV2Compatible,
        fromFrontOffice,
        hasALiveVersion,
        hasChanges,
        id,
        instance?.homePage,
        isContentInSpaceEnabled,
        isLayoutEnabled,
        redirect,
        spaceData?.slug,
        spaceId,
        translateKey,
        translateObject,
    ]);

    return { closeContentEditor, closeConfirmDialogProps: closeConfirmDialog.props };
}
